import { Dispatch, FC, SetStateAction } from 'react';

import { AccountCreateError } from 'utils/constants/accounts';
import { Permissions } from 'utils/constants/permissions';
import { getTomorrowDate } from 'utils/date';

import { Cell, CellText, Checkbox, NumberInput, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { TITLE_ERRORS_TRL } from 'components/AccountCreate/components/AccountForm/AccountForm';
import { AccountFormItem } from 'components/AccountCreate/components/AccountFormItem';
import { DateSelect } from 'components/common/DateSelect/DateSelect';

import {
    setAccountsExpiredDate,
    setAccountsIsContractSettings,
    setAccountsPricePerClick,
    setCreateAccountErrors,
} from 'store/models/accounts';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    showContractSettings: 'AccountCreate.AccountForm.ContractFields.showContractSettings',
    pricePerClick: {
        label: 'AccountCreate.AccountForm.ContractFields.pricePerClick.label',
        placeholder: 'AccountCreate.AccountForm.ContractFields.pricePerClick.placeholder',
        hint: 'AccountCreate.AccountForm.ContractFields.pricePerClick.hint',
        error: 'AccountCreate.AccountForm.ContractFields.pricePerClick.error',
    },
    expiredDate: {
        label: 'AccountCreate.AccountForm.ContractFields.expiredDate.label',
        hint: 'AccountCreate.AccountForm.ContractFields.expiredDate.hint',
    },
};

export const ContractFields: FC<{
    accountContractError: AccountCreateError | null;
    setAccountContractError: Dispatch<SetStateAction<AccountCreateError | null>>;
}> = ({ accountContractError, setAccountContractError }) => {
    const { isContractSettings, isContractExist, expiredDate, pricePerClick, accountErrors } = useSelector(
        ({ accounts }) => accounts
    );

    const permissions = useSelector((state) => state.user.permissions);

    const accountId = useSelector((state) => state.accounts.accountId);

    const dispatch = useDispatch();
    const trl = useTrls();

    const isExpired = expiredDate ? expiredDate < new Date() : false;

    if (!isContractExist && (accountId || !permissions.includes(Permissions.ViewAccountCreateContractFields))) {
        return null;
    }

    return (
        <>
            {!accountId && (
                <>
                    <Cell
                        left={
                            <Checkbox
                                onChange={() => {
                                    dispatch(setAccountsIsContractSettings(!isContractSettings));
                                }}
                                checked={isContractSettings}
                                data-qa="show-contract-settings-checkbox"
                            />
                        }
                    >
                        <CellText>{trl(TrlKeys.showContractSettings)}</CellText>
                    </Cell>
                    <VSpacing default={isContractSettings ? 16 : 32} />
                </>
            )}
            {(isContractSettings || accountId) && (
                <>
                    <AccountFormItem
                        label={trl(TrlKeys.pricePerClick.label)}
                        element={
                            <NumberInput
                                value={pricePerClick ? String(pricePerClick) : ''}
                                placeholder={trl(TrlKeys.pricePerClick.placeholder)}
                                disabled={isExpired}
                                onChange={(value) => {
                                    dispatch(setAccountsPricePerClick(Number(value)));
                                    if (accountContractError) {
                                        dispatch(
                                            setCreateAccountErrors(
                                                accountErrors.filter((key) => key !== accountContractError)
                                            )
                                        );
                                        setAccountContractError(null);
                                    }
                                }}
                                invalid={!!accountContractError}
                                data-qa="price-per-click"
                            />
                        }
                        hint={trl(TrlKeys.pricePerClick.hint)}
                        error={accountContractError ? trl(TITLE_ERRORS_TRL[accountContractError]) : null}
                        isRequired
                    />
                    <AccountFormItem
                        label={trl(TrlKeys.expiredDate.label)}
                        element={
                            <DateSelect
                                placeholder={trl('admin.actsReport.filter.input')}
                                selectedDate={expiredDate || undefined}
                                onDateSelect={(dateString) => {
                                    if (dateString) {
                                        dispatch(setAccountsExpiredDate(new Date(dateString)));
                                    }
                                }}
                                minDate={getTomorrowDate()}
                                inputDisabled={isExpired}
                                data-qa="expired-date-input"
                            />
                        }
                        hint={trl(TrlKeys.expiredDate.hint)}
                    />
                </>
            )}
        </>
    );
};
